import React, { useEffect, useRef, useState } from 'react';
import * as _ from 'lodash';
import classNames from 'classnames';
import { Element, ScrollLink, scrollSpy } from 'react-scroll';
import CloseBigIcon from '../../assets/images/close-big-15x15.svg';
import ArrowDownMiddleIcon from '../../assets/images/arrow-down-middle-15x15.svg';
import DownloadIcon from '../../assets/images/download-15x15.svg';
import UploadIcon from '../../assets/images/file-upload-32x32.svg';
import LinkIcon from '../../assets/images/link-16x16.svg';
import MenuVIcon from '../../assets/images/menu-v-15x15.svg';
import Dropdown from '../../components/Dropdown';
import Tooltip from '../../components/Tooltip';
import { hideDropdown } from '../../redux/actions/general/dropdown';
import { __, setTableParams } from '../../core/utils';
import Details from './Details';
import Versions from './Versions';
import LinkedItems from './LinkedItems';
import History from './History';
import FileTags from './FileTags';
import styles from '../../styles/flyouts/files/FileFlyout.scss';

const SECTIONS = ['details', 'versions', 'linked-items', 'tags', 'history'];
const TabLink = ScrollLink((props) => (
  <span
    className={props.className}
    onClick={(event) => {
      props.onSelect(props.target);
      props.onClick(event);
    }}
  >
    {props.children}
  </span>
));

const FileFlyout = ({
  data,
  onDownload,
  onOpenEdit,
  onOpenUploadVersion,
  onOpenLink,
  onArchive,
  onUnlink,
  onUpdateTags,
}) => {
  const [collapsedSections, setCollapsedSections] = useState([]);
  const [highlightedSection, setHighlightedSection] = useState(null);
  const contentRef = useRef(null);
  const highlightTimeout = useRef(null);

  useEffect(() => {
    scrollSpy.update();
    setCollapsedSections(['history']);
  }, [data.id]);

  useEffect(
    () => () => {
      clearTimeout(highlightTimeout.current);
    },
    [],
  );

  const toggleSection = (section) => {
    setCollapsedSections((current) => _.xor(current, [section]));
  };

  const openSection = (section) => {
    setCollapsedSections((current) => _.without(current, section));
    setHighlightedSection(section);
    clearTimeout(highlightTimeout.current);
    highlightTimeout.current = setTimeout(
      () => setHighlightedSection(null),
      200,
    );
  };

  const toggleAllSections = () => {
    setCollapsedSections((current) =>
      current.length === SECTIONS.length ? [] : SECTIONS,
    );

    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  const close = () =>
    setTimeout(() => setTableParams('files', { clicked_row: undefined }), 100);

  const action = (tooltip, icon, onClick) => (
    <div className={styles.action} onClick={onClick}>
      <Tooltip text={tooltip}>{icon}</Tooltip>
    </div>
  );

  const Tab = ({ target, children }) => (
    <TabLink
      target={target}
      activeClass={styles.active}
      to={target}
      spy={true}
      smooth={true}
      duration={500}
      container={contentRef.current}
      onSelect={openSection}
    >
      {children}
    </TabLink>
  );

  return (
    <div className={styles.preview}>
      <div className={styles.header}>
        <div className={styles.title}>
          <div>
            <span className={styles.fileType}>{_.toUpper(data.extension)}</span>
            <span>{data.file_name}</span>
            {data.archived && (
              <span className={styles.archived}>
                {__('attachments.menu.archived')}
              </span>
            )}
          </div>
          <CloseBigIcon onClick={close} />
        </div>
        <div className={styles.actions}>
          {action(
            __('attachments.menu.download', {}, 'Download'),
            <DownloadIcon />,
            () => onDownload(data, true),
          )}
          {action(
            __('attachments.menu.upload-new-version', {}, 'Upload new version'),
            <UploadIcon />,
            () => onOpenUploadVersion(data),
          )}
          {action(__('files.flyout.link-to', {}, 'Link to'), <LinkIcon />, () =>
            onOpenLink(data),
          )}
          <Dropdown
            top={28}
            right={-14}
            name={'file_flyout_actions_' + data.id}
            wrapperClassName={styles.moreWrapper}
            dropdownClassName={styles.moreDropdown}
            header={
              <div className={styles.menuHeader}>
                {__('attachments.menu.file', {}, 'File')}
              </div>
            }
            content={
              <div className={styles.menuContent}>
                <div
                  onClick={() => {
                    hideDropdown();
                    onOpenEdit(data);
                  }}
                >
                  {__('files.flyout.rename', {}, 'Rename')}
                </div>
                <div
                  onClick={() => {
                    hideDropdown();
                    onArchive(data);
                  }}
                >
                  {data.archived
                    ? __('attachments.menu.unarchive', {}, 'Unarchive')
                    : __('attachments.menu.archive', {}, 'Archive')}
                </div>
              </div>
            }
          >
            <MenuVIcon className={styles.moreIcon} />
          </Dropdown>
        </div>
      </div>

      <div className={styles.tabsWrapper}>
        <ArrowDownMiddleIcon
          className={classNames(
            styles.collapseAll,
            collapsedSections.length === SECTIONS.length && styles.collapsed,
          )}
          onClick={toggleAllSections}
        />
        <div className={styles.tabs}>
          <Tab target='details'>
            {__('files.flyout.details', {}, 'Details')}
          </Tab>
          <Tab target='versions'>
            {__('attachments.menu.versions', {}, 'Versions')}
            {!_.isEmpty(data.versions) && ' (' + data.versions.length + ')'}
          </Tab>
          <Tab target='linked-items'>
            {__('files.flyout.linked-items', {}, 'Linked items')}
            {!_.isEmpty(data.sources) && ' (' + data.sources.length + ')'}
          </Tab>
          <Tab target='tags'>
            {__('Tags')}
            {!_.isEmpty(data.tags) && ' (' + data.tags.length + ')'}
          </Tab>
          <Tab target='history'>{__('task.flyout.history')}</Tab>
        </div>
      </div>

      <div className={styles.content} ref={contentRef}>
        <Section
          name='details'
          title={__('files.flyout.details', {}, 'Details')}
          collapsedSections={collapsedSections}
          highlightedSection={highlightedSection}
          onToggle={toggleSection}
        >
          <Details
            data={data}
            collapsed={_.includes(collapsedSections, 'details')}
            onDownload={onDownload}
          />
        </Section>
        <Section
          name='versions'
          title={__('attachments.menu.versions', {}, 'Versions')}
          count={data.versions.length}
          collapsedSections={collapsedSections}
          highlightedSection={highlightedSection}
          onToggle={toggleSection}
        >
          <Versions
            versions={data.versions}
            collapsed={_.includes(collapsedSections, 'versions')}
            onDownload={onDownload}
          />
        </Section>
        <Section
          name='linked-items'
          title={__('files.flyout.linked-items', {}, 'Linked items')}
          count={data.sources.length}
          collapsedSections={collapsedSections}
          highlightedSection={highlightedSection}
          onToggle={toggleSection}
        >
          <LinkedItems
            sources={data.sources}
            collapsed={_.includes(collapsedSections, 'linked-items')}
            onUnlink={(source) => onUnlink(data, source)}
          />
        </Section>
        <Section
          name='tags'
          title={__('Tags')}
          count={_.size(data.tags)}
          collapsedSections={collapsedSections}
          highlightedSection={highlightedSection}
          onToggle={toggleSection}
        >
          <FileTags
            data={data}
            collapsed={_.includes(collapsedSections, 'tags')}
            initialValues={{
              tags: data.tags || [],
            }}
            onSubmit={(formData) => onUpdateTags(data, formData.tags || [])}
          />
        </Section>
        <Section
          name='history'
          title={__('task.flyout.history')}
          collapsedSections={collapsedSections}
          highlightedSection={highlightedSection}
          onToggle={toggleSection}
        >
          <History
            history={data.history}
            collapsed={_.includes(collapsedSections, 'history')}
          />
        </Section>
      </div>
    </div>
  );
};

const Section = ({
  name,
  title,
  count,
  collapsedSections,
  highlightedSection,
  onToggle,
  children,
}) => (
  <Element
    name={name}
    className={classNames(
      styles.section,
      highlightedSection === name && styles.highlighted,
    )}
  >
    <div className={styles.sectionTitle} onClick={() => onToggle(name)}>
      <ArrowDownMiddleIcon
        className={classNames(
          styles.collapse,
          _.includes(collapsedSections, name) && styles.collapsed,
        )}
      />
      {title}
      {!!count && ' (' + count + ')'}
    </div>
    {children}
  </Element>
);

FileFlyout.defaultProps = {
  data: {
    versions: [],
    sources: [],
    history: [],
    folders: [],
    tags: [],
  },
};

export default FileFlyout;
